type Handler<Data> = (entry: IntersectionObserverEntry, data?: Data) => void;

export class VisibilityTracker<Data> {
    // стандартный обсервер пападания дом-элемента на экран
    private observer: IntersectionObserver;

    // список обработчиков для элементов, попавших на экран (хранится как пара [обработчик, задержка мс])
    private handlersShown: [Handler<Data>, number][] = [];

    // список обработчиков для элементов, скрывшихся с экрана
    private handlersHidden: Handler<Data>[] = [];

    // список таймеров для обработчиков, которые ждут, когда пройдет указанная задержка
    private delayedHandlers: Map<Element, NodeJS.Timeout[]> = new Map();

    // дополнительные данные, которые нужно ассоциировать с дом-элементов (например, карточка промпта – данные промпта)
    private entries = new Map<Element, Data>();

    constructor(threshold: number) {
        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    // для элементов, попавших на экран вызываем все обработчики показа
                    if (entry.isIntersecting) {
                        this.handleEntryShown(entry);

                        return;
                    }

                    // для пропавших с экрана вызываем все обработчики скрытия
                    this.handleEntryHidden(entry);
                });
            },
            {
                root: null, // null означает, что отслеживаем видимость внутри viewport (окна браузера)
                threshold, // какая часть элемента должна оказаться на экране, чтобы считать его видимым (0.0 - 1.0)
            },
        );
    }

    /**
     * Добавить обработчик
     * @param event тип события
     * @param handler обработчик события
     * @param [delay=0] сколько миллисекунд элемент должен находиться на экране, чтобы считать его видимым
     */
    public addHandler(event: 'shown' | 'hidden', handler: Handler<Data>, delay: number = 0) {
        if (event === 'shown') {
            this.handlersShown.push([handler, delay]);
        } else {
            this.handlersHidden.push(handler);
        }
    }

    /**
     * Удалить обработчик
     */
    public removeHandler(handlerToRemove: Handler<Data>) {
        this.handlersShown = this.handlersShown.filter(handler => handler[0] !== handlerToRemove);
        this.handlersHidden = this.handlersHidden.filter(handler => handler !== handlerToRemove);
    }

    private handleEntryShown(entry: IntersectionObserverEntry) {
        const data = this.entries.get(entry.target);

        for (let [handler, delay] of this.handlersShown) {
            // если задержка не указана, вызываем обработчик сразу
            if (!delay) {
                handler(entry, data);
                // иначе создаем таймер и добавляем его в список
            } else {
                const timer = setTimeout(() => {
                    handler(entry, data);
                }, delay);

                this.delayedHandlers.set(entry.target, [...(this.delayedHandlers.get(entry.target) ?? []), timer]);
            }
        }
    }

    private handleEntryHidden(entry: IntersectionObserverEntry) {
        const data = this.entries.get(entry.target);
        this.handlersHidden.forEach(handler => handler(entry, data));

        // получаем все отложенные обработчики для этого элемента
        const delayedHandlers = this.delayedHandlers.get(entry.target);

        if (!delayedHandlers) {
            return;
        }

        // отменяем все отложенные обработчики для этого элемента
        for (let timer of delayedHandlers) {
            clearTimeout(timer);
        }

        // удаляем запись из Map после очистки таймеров
        this.delayedHandlers.delete(entry.target);
    }

    /**
     * Зарегистировать элемент для отслеживания
     * @param element дом-элемент, за которым нужно следить
     * @param additionalData любые ассоциированные с элементом данные (придут в обработчик вторым аргументом)
     */
    public observe(element: Element, additionalData: Data) {
        this.observer.observe(element);
        this.entries.set(element, additionalData);
    }

    /**
     * Перестать отслеживать элемент
     * @param element дом-элемент, за которым больше не нужно следить
     */
    public unobserve(element: Element) {
        this.observer.unobserve(element);
        this.entries.delete(element);
    }
}

/**
 * Обертка для безопасного создания обсервера просмотренных промптов с учетом SSR
 */
export function createVisibilityTracker<Data>(threshold: number) {
    if (typeof window === 'undefined') {
        return null;
    }

    return new VisibilityTracker<Data>(threshold);
}
